// Compute a padded axis range across a list of raw values.
// Used to give a group of charts one shared scale instead of each
// autoscaling independently to its own data.

// Delta plots always show a little space below zero, even when every value
// is positive, so that "no change" reads as a position on the axis rather
// than as the bottom of it.
const DELTA_NEGATIVE_FLOOR = -2;

// Used when the values give the range no width of its own: no finite values at
// all, or every value identical. These charts are now given an explicit Plotly
// `range` instead of autoscaling, so a degenerate range is a blank plot rather
// than a slightly wrong one.
const EMPTY_RANGE: [number, number] = [0, 1];
const MIN_PADDING = 1;

export function computeAxisRange(
  values: number[],
  options?: { showNegativeMargin?: boolean },
): [number, number] {
  let min = Infinity;
  let max = -Infinity;
  values.forEach((v) => {
    if (v != null && isFinite(v)) {
      min = Math.min(min, v);
      max = Math.max(max, v);
    }
  });

  // Nothing plottable: hand back a plain unit range rather than
  // [Infinity, -Infinity], which Plotly cannot lay an axis out from.
  if (!isFinite(min) || !isFinite(max)) return EMPTY_RANGE;

  if (options?.showNegativeMargin && min >= 0) {
    min = DELTA_NEGATIVE_FLOOR;
  }

  // A single distinct value has no span to take 10% of, and a zero-width
  // `range` is a Plotly axis with nothing to draw. Fall back to a margin
  // proportional to the value itself, with a floor for values near zero.
  const span = max - min;
  const padding = span > 0
    ? span * 0.1
    : Math.max(Math.abs(max) * 0.1, MIN_PADDING);
  return [min - padding, max + padding];
}
